import { Component, OnInit, HostListener, ElementRef } from '@angular/core';
import { HsgOidcManagerService, HsgSubNavService, HsgHeaderMenuLink } from '@hhsg/hsg-asc';
import { takeUntil } from 'rxjs/operators';

import { Constants } from '../shared/utilities/constants.util';
import { PermissionsService } from '../shared/services/permissions.service';
import { RouteUtil } from '../shared/utilities/route.util';
import { Subject } from 'rxjs';
import { UserPermissions } from '../shared/models/user-permissions.model';
import { Router, NavigationEnd } from '@angular/router';
import { GoogleAnalyticsService } from '../shared/services/google-analytics.service';
import { ConfigService } from 'src/shared/services/config.service';
import * as workerTimers from 'worker-timers';
import { MiscUtil } from 'src/shared/utilities/misc.util';

declare let gtag: Function;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit { 
    adminLinks: { route: string, name: string }[] = [];
    importLinks: { route: string, name: string }[] = [];
    reportsLinks: { route: string, name: string }[] = [];
    sectionLinks: { route: string, name: string }[] = [];
    workflowLinks: { route: string, name: string }[] = [];

    private _unsubscribe$ = new Subject();            

    canRefresh = true;
    extendingSession = false;
    sessionExpiring = false;
    sessionSecondsLeft:number;
    sessionModalShowInSec;number;
    sessionExtensionSeconds = 0;
    allowRefresh: Function = MiscUtil.DelayFunctionExecution(() => { this.canRefresh = true; }, 10); //this._configService.appSettings.orgtxSilentRefreshDelay);
    isSessionTimeoutEnable = false;    
    sessionMessage:string='';
    private readonly sessionDuration: number;
    private idleId: number;

    constructor(private subNav: HsgSubNavService, private permissionsService: PermissionsService,private router: Router, private googleAnalyticsService: GoogleAnalyticsService, private configService: ConfigService, private elementRef: ElementRef, private _router: Router,private _oidcManager: HsgOidcManagerService) { 

        this.subNav.setSubNavData({
            title: "",
            links: [] 
        });

        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {                               
                gtag('config', configService.appSettings.GATrackingId, { 'page_path': event.urlAfterRedirects, 'cookie_flags': 'Secure' });                  
                let routeValues = this.router.url.split("/");   
                this.googleAnalyticsService.trackEvent(
                    Constants.GANavigateEvent,
                    Constants.GANavigateEvent,
                    routeValues[Constants.GAEventCategoryIndex],
                    routeValues[Constants.GAEventLabelIndex],
                    this.router.url        
                );              
            }
        });     
        this.sessionDuration = this.configService.appSettings.WorkforceIdleTimeout/1000;
        this.sessionModalShowInSec = this.configService.appSettings.SessionResetModalShowInSec;
        this.isSessionTimeoutEnable = this.configService.appSettings.IsSessionTimeoutEnable;   
    }

    ngOnInit() {
        this.permissionsService.userPermissions$
            .pipe(takeUntil(this._unsubscribe$))
			.subscribe((user: UserPermissions) => {
                this.adminLinks = this.getPageLinks(user, Constants.AdminPages);
                this.importLinks = this.getPageLinks(user, Constants.ImportPages);
                this.reportsLinks = this.getPageLinks(user, Constants.ReportsPages);
                this.workflowLinks = this.getPageLinks(user, Constants.WorkflowPages);
                this.getSectionLinks();
            });
            this.sessionSecondsLeft = this.configService.appSettings.WorkforceIdleTimeout/1000;
            this.sessionModalShowInSec = this.configService.appSettings.SessionResetModalShowInSec;
            if(this.isSessionTimeoutEnable)
            this.runTimer();
    }

    ngAfterViewInit() {
        let script = document.createElement("script");
        script.type = "text/javascript";
        script.src = this.configService.appSettings.GATrackingBaseUrl + this.configService.appSettings.GATrackingId;
        this.elementRef.nativeElement.appendChild(script);
        gtag('js', new Date());
        gtag('config', this.configService.appSettings.GATrackingId, {'cookie_flags': 'Secure' });
    }

    getPageLinks(user: UserPermissions, pages: string[][]): any {
        let links: { route: string, name: string }[] = [];

        pages.forEach((page) => {
            let pageName = page[0];
            let displayName = page[2];

            if (RouteUtil.isPageViewable(user, pageName)) {
                links.push({ route: RouteUtil.findPageRoute(user, pageName), name: displayName });
            }
        });

        return links;
    }

    getSectionLinks() {
        let adminMenuLink = {
            name: "Admin",
            route: "#",
            menuLinks: this.adminLinks  
        };

        let importsMenuLink = {
            name: "Imports",
            route: "#",
            menuLinks: this.importLinks  
        };
        
        let reportsMenuLink = {
          name: "Reports",
          route: "#",
          menuLinks: this.reportsLinks  
      };

        let workflowMenuLink = {
            name: "Staffing Plan",
            route: "#",
            menuLinks: [ { route: "/workflow/planner", name: "Enter Staffing Plan" }]
        };

        this.sectionLinks = [];
        
        if (this.adminLinks.length > 0) {
            this.sectionLinks.push(adminMenuLink);
        }

        if (this.importLinks.length > 0) {
            this.sectionLinks.push(importsMenuLink);
        }

        if (this.reportsLinks.length > 0) {
            this.sectionLinks.push(reportsMenuLink);
        }

        this.sectionLinks.push(workflowMenuLink);
    }

    get showNav(): boolean {
        var subNavData = this.subNav.getSubNavData();

        return subNavData && !!subNavData.links.length;
    }

    @HostListener('document:click', ['$event'])
    @HostListener('document:keydown', ['$event'])
    @HostListener('document:wheel', ['$event'])
    async keepAlive(event: any): Promise<void> {
        if (this.canRefresh) {
            this.extendSession();
        }

    }

    extendSession() {
        this.extendingSession = true;
        let token = this._oidcManager.getToken();

        if (token) {
            // Disable refresh.
            this.canRefresh = false;

            this._oidcManager.keepAlive(null).then(() => {
                // Kick off the timer that will wait before enabling the next refresh.
                this.allowRefresh();

                let token = this._oidcManager.getToken();

                if (token) {
                    sessionStorage.setItem(Constants.TokenName, token);
                }

                this.resetSessionSeconds();
            })
            .catch(error => console.error('Promise ended in rejection while fetching token.'));
        }
    }

    resetSessionSeconds() {
        this.extendingSession = false;
        this.sessionExpiring = false;
        this.sessionSecondsLeft = this.sessionDuration;
        this.sessionExtensionSeconds = 0;
    }

    // A recursive function that calls itself every second to decrement
    // the timer and check to see if the session ending modal should be shown.
    runTimer: Function = () => {    
         if (this.idleId) {
             workerTimers.clearTimeout(this.idleId);
         }
        this.idleId = workerTimers.setTimeout(async () => {
            if (this.sessionSecondsLeft != this.sessionDuration && this.sessionSecondsLeft % 60 == 0) {
                console.log("Session minutes remaining smt: " + (this.sessionSecondsLeft / 60).toFixed(0));
            }

            if (this.sessionSecondsLeft > 0 && this.sessionExtensionSeconds < 10) {
                if (this.extendingSession) {
                    this.sessionExtensionSeconds++;
                }
                else {
                    this.sessionSecondsLeft--;
                }
            }
            else {
                this.resetSessionSeconds();
                await this._router.navigateByUrl(`logout`);
            }

            if (this.sessionSecondsLeft <= this.sessionModalShowInSec) {
                this.sessionExpiring = true;
            }

            if (this.sessionExpiring) {
                if (this.extendingSession) {
                    let dots = ["", ".", "..", "..."];
                    this.sessionMessage = "Requesting a session extension";
                    this.sessionMessage += dots[this.sessionExtensionSeconds % 4];
                }
                else {
                    this.sessionMessage = "Your session will expire in " + this.sessionSecondsLeft;

                    if (this.sessionSecondsLeft == 1) {
                        this.sessionMessage += " second.";
                    }
                    else {
                        this.sessionMessage += " seconds.";
                    }
                }
            }

            this.runTimer();
        },
            1000);
    }
}
